import * as actions from './TransactionsActions';
import { DEFAULT_ACCOUNT_ID, DEFAULT_PERIOD_ID, BOTH_FILTER, VIEWS } from './TransactionsConstants';
import { SET_CURRENT_CLIENT } from '../AdviserArea/AdviserAreaActions';
import cloneDeep from 'lodash/cloneDeep';

const DRAFT_TRANSACTION_DEFAULT = {
  account: { value: '', label: '' },
  amount: null,
  category: '',
  day: null,
  description: '',
  month: null,
  year: null,
  isManuallyCreated: true,
  isNegative: false,
};

const DEFAULT_PAGINATION = { currentPage: 0, totalPagesCount: 1 };
const DEFAULT_QUERY = '';
export const DEFAULT_EXPENSES_TITLE = 'Expenses';
export const DEFAULT_INCOME_TITLE = 'Income';

const initialState = {
  currentClientId: null,
  currentAccount: { accountId: DEFAULT_ACCOUNT_ID, accountName: null },
  currentPeriodId: DEFAULT_PERIOD_ID,
  expenseCategoryFilter: '',
  transactions: null,
  accounts: null,
  pagination: DEFAULT_PAGINATION,
  expenseCategoryOptions: null,
  periodOptions: null,
  query: DEFAULT_QUERY,
  filterOpen: false,
  modal: {
    isAddUpdateOpen: false,
    isDeleteOpen: false,
  },
  isEdit: false,
  draftTransaction: DRAFT_TRANSACTION_DEFAULT,
  errors: [],
  expensesData: {
    defaultTitle: DEFAULT_EXPENSES_TITLE,
    title: DEFAULT_EXPENSES_TITLE,
    total: null,
    series: [],
    isVisible: false,
  },
  incomeData: {
    defaultTitle: DEFAULT_INCOME_TITLE,
    title: DEFAULT_INCOME_TITLE,
    total: null,
    series: [],
    isVisible: false,
  },
  selectedCategory: null,
  currentView: VIEWS.LIST,
  incomeExpense: BOTH_FILTER,
};

export const moveItemInArray = (arr, initialPosition, finalPosition) =>
  arr.reduce((prev, current, idx, self) => {
    if (initialPosition === finalPosition) {
      prev.push(current);
    }
    if (idx === initialPosition) {
      return prev;
    }
    if (initialPosition < finalPosition) {
      prev.push(current);
    }
    if (idx === finalPosition) {
      prev.push(self[initialPosition]);
    }
    if (initialPosition > finalPosition) {
      prev.push(current);
    }
    return prev;
  }, []);

export default (state = initialState, { type, currentClient, ...action }) => {
  switch (type) {
    case SET_CURRENT_CLIENT: {
      const clientId = currentClient?.Id ?? null;

      if (clientId && clientId === state.currentClientId) {
        return state;
      }

      return {
        ...cloneDeep(initialState),
        currentClientId: clientId,
      };
    }
    case actions.SET_CURRENT_VIEW: {
      const commonState = {
        ...state,
        currentView: action.currentView,
      };

      if (action.currentView === VIEWS.LIST) {
        return {
          ...commonState,
          modal: initialState.modal,
          isEdit: false,
          draftTransaction: DRAFT_TRANSACTION_DEFAULT,
          errors: initialState.errors,
        };
      }

      if (action.currentView === VIEWS.EDIT) {
        return {
          ...commonState,
          modal: {
            ...initialState.modal,
            isAddUpdateOpen: true,
          },
        };
      }

      return commonState;
    }
    case actions.SET_QUERY:
      return {
        ...state,
        query: action.query,
      };
    case actions.SET_OR_APPEND_TRANSACTIONS: {
      const {
        transactions,
        accounts,
        expenseCategoryOptions,
        accountId,
        accountName,
        currentPage,
        totalPagesCount,
        periodOptions,
        periodId,
        expenseCategoryFilter,
      } = action;

      if (currentPage === 1) {
        return {
          ...state,
          transactions,
          currentAccount: { accountId, accountName },
          accounts,
          pagination: { currentPage, totalPagesCount },
          expenseCategoryOptions,
          periodOptions,
          currentPeriodId: periodId,
          expenseCategoryFilter,
        };
      }

      return {
        ...state,
        transactions: [...state.transactions, ...transactions],
        currentAccount: { accountId, accountName },
        accounts,
        pagination: { currentPage, totalPagesCount },
        expenseCategoryOptions,
        periodOptions,
        currentPeriodId: periodId,
        expenseCategoryFilter,
      };
    }
    case actions.ON_FILTER_OPEN: {
      return {
        ...state,
        filterOpen: true,
      };
    }
    case actions.ON_FILTER_CLOSE: {
      return {
        ...state,
        filterOpen: false,
      };
    }
    case actions.SUCCESS_ADD_OR_UPDATE_TRANSACTION: {
      const transactionIndex = state.transactions.findIndex(
        transaction => transaction.Id.toString() === action.transaction.Id.toString(),
      );

      if (transactionIndex >= 0) {
        return {
          ...state,
          transactions: [
            ...state.transactions.slice(0, transactionIndex),
            action.transaction,
            ...state.transactions.slice(transactionIndex),
          ],
          draftTransaction: DRAFT_TRANSACTION_DEFAULT,
          cashflowTotals: initialState.cashflowTotals,
          pagination: DEFAULT_PAGINATION,
          query: DEFAULT_QUERY,
        };
      }
      return {
        ...state,
        transactions: [...state.transactions, action.transaction],
        draftTransaction: DRAFT_TRANSACTION_DEFAULT,
        cashflowTotals: initialState.cashflowTotals,
        pagination: DEFAULT_PAGINATION,
        query: DEFAULT_QUERY,
      };
    }
    case actions.OPEN_DELETE_MODAL:
      return {
        ...state,
        modal: {
          ...initialState.modal,
          isDeleteOpen: true,
        },
      };
    case actions.SET_DRAFT_TRANSACTION: {
      const {
        AccountId,
        AccountName,
        Balance,
        Category,
        Date: transactionDate,
        Description,
        IsManuallyCreated,
        NegativeBalance,
        ...rest
      } = action.transaction;

      const date = new Date(transactionDate);

      const draftTransaction = {
        ...DRAFT_TRANSACTION_DEFAULT,
        ...rest,
        day: date.getUTCDate(),
        month: date.getUTCMonth() + 1,
        year: date.getUTCFullYear(),
        account: { value: AccountId, label: AccountName },
        amount: Balance,
        category: Category,
        description: Description,
        isManuallyCreated: IsManuallyCreated,
        isNegative: NegativeBalance,
      };

      return {
        ...state,
        isEdit: true,
        draftTransaction,
      };
    }
    case actions.UPDATE_DRAFT_TRANSACTION:
      return {
        ...state,
        draftTransaction: action.data,
      };
    case actions.SUCCESS_DELETE: {
      return {
        ...state,
        transactions: state.transactions.filter(({ Id }) => action.id !== Id),
        cashflowTotals: initialState.cashflowTotals,
        pagination: DEFAULT_PAGINATION,
        query: DEFAULT_QUERY,
      };
    }
    case actions.SET_ERRORS:
      return {
        ...state,
        errors: action.errors,
      };
    case actions.CLEAR_ERRORS:
      return {
        ...state,
        errors: initialState.errors,
      };
    case actions.SET_EXPENSES_CHART_DATA: {
      return {
        ...state,
        expensesData: {
          ...state.expensesData,
          ...action.data,
          isVisible: true,
        },
      };
    }
    case actions.REMOVE_EXPENSES_CHART_DATA:
      return {
        ...state,
        expensesData: {
          ...initialState.expensesData,
          isVisible: false,
        },
      };
    case actions.SET_INCOME_CHART_DATA: {
      return {
        ...state,
        incomeData: {
          ...state.incomeData,
          ...action.data,
          isVisible: true,
        },
      };
    }
    case actions.REMOVE_INCOME_CHART_DATA:
      return {
        ...state,
        incomeData: {
          ...initialState.incomeData,
          isVisible: false,
        },
      };
    case actions.SELECT_CATEGORY:
      return {
        ...state,
        selectedCategory: action.categoryName,
        incomeExpense: action.incomeExpense,
      };
    case actions.START_UPDATE_TRANSACTION_REQUEST:
      return {
        ...state,
        transactions: state.transactions.map(transaction => {
          if (transaction.Id === action.payload.Id) {
            return {
              ...transaction,
              isLoading: true,
            };
          }
          return transaction;
        }),
      };
    case actions.END_UPDATE_TRANSACTION_REQUEST:
      return {
        ...state,
        transactions: state.transactions.map(transaction => {
          if (transaction.Id === action.payload.Id) {
            return {
              ...transaction,
              ...action.payload,
              isLoading: false,
            };
          }
          return transaction;
        }),
      };
    default:
      return state;
  }
};
